/**
 * XP Calculation Utilities
 * Uses XP_PER_LEVEL and LEVELS from data/achievements.ts
 */

import { XP_PER_LEVEL, LEVELS, calculateLevel } from '../data/achievements';

/**
 * Calculate XP earned from reading words
 * Formula: floor(words / 100)
 */
export function calculateWordsXP(wordsRead: number): number {
  return Math.floor(wordsRead / 100);
}

/**
 * Calculate XP earned from session completion
 * Formula: 10 + floor(wpm / 10)
 */
export function calculateSessionXP(wpm: number): number {
  return 10 + Math.floor(wpm / 10);
}

/**
 * Calculate total XP for a reading session
 */
export function calculateXPForSession(wordsRead: number, wpm: number): number {
  const wordsXP = calculateWordsXP(wordsRead);
  const sessionXP = calculateSessionXP(wpm);
  return wordsXP + sessionXP;
}

/**
 * Get streak multiplier based on streak days
 */
export function getStreakMultiplier(streakDays: number): number {
  if (streakDays >= 30) return 2.0;
  if (streakDays >= 14) return 1.5;
  if (streakDays >= 7) return 1.25;
  return 1.0;
}

/**
 * Apply streak multiplier to base XP
 */
export function applyStreakMultiplier(baseXP: number, streakDays: number): number {
  const multiplier = getStreakMultiplier(streakDays);
  return Math.floor(baseXP * multiplier);
}

/**
 * Calculate XP with all bonuses
 */
export function calculateTotalXP(
  wordsRead: number,
  wpm: number,
  streakDays: number,
  options: {
    isFirstSessionOfDay?: boolean;
    isNewMaxWPM?: boolean;
    isPersonalBest?: boolean;
    bookCompletion?: number; // word count if book completed
  } = {}
): number {
  let totalXP = calculateXPForSession(wordsRead, wpm);

  // First session of day bonus
  if (options.isFirstSessionOfDay) {
    totalXP += 15;
  }

  // Speed milestone bonus
  if (options.isNewMaxWPM) {
    totalXP += 25;
  }

  // Personal best bonus
  if (options.isPersonalBest) {
    totalXP += 50;
  }

  // Book completion bonus
  if (options.bookCompletion && options.bookCompletion > 0) {
    totalXP += 50 + Math.floor(options.bookCompletion / 1000);
  }

  // Apply streak multiplier
  totalXP = applyStreakMultiplier(totalXP, streakDays);

  return totalXP;
}

/**
 * Get level info from total XP
 */
export function getLevelInfo(totalXP: number) {
  return calculateLevel(totalXP);
}

/**
 * Get XP needed for next level
 */
export function getXPToNextLevel(totalXP: number): number {
  const levelInfo = calculateLevel(totalXP);
  return levelInfo.nextXP;
}

/**
 * Check if user should level up
 */
export function shouldLevelUp(currentXP: number, newXP: number): boolean {
  const currentLevel = calculateLevel(currentXP).level;
  const newLevel = calculateLevel(currentXP + newXP).level;
  return newLevel > currentLevel;
}

/**
 * Daily challenge XP rewards
 */
export const CHALLENGE_XP = {
  wordSprint: 15,      // 500 words in one session
  speedRun: 20,         // 400+ WPM
  nightOwl: 10,         // Session after 10 PM
  earlyBird: 10,        // Session before 7 AM
  volumeKing: 25,        // 2,000 words today
  streakDefender: 30,   // Maintain 7+ day streak
} as const;

/**
 * Calculate challenge XP
 */
export function calculateChallengeXP(challengeId: string): number {
  return CHALLENGE_XP[challengeId as keyof typeof CHALLENGE_XP] ?? 0;
}
